<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useMapGetter, useStore } from 'dashboard/composables/store.js';
import { useAccount } from 'dashboard/composables/useAccount';
import wootConstants from 'dashboard/constants/globals';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';

// Ellie Desk: status lives on the header pill instead of inside the sort menu
const emit = defineEmits(['changeFilter']);

const store = useStore();
const { t } = useI18n();
const { updateUISettings } = useUISettings();
const { isCloudFeatureEnabled } = useAccount();

const chatStatusFilter = useMapGetter('getChatStatusFilter');
const chatSortFilter = useMapGetter('getChatSortFilter');

const currentStatus = computed(
  () => chatStatusFilter.value || wootConstants.STATUS_TYPE.OPEN
);

const statusOptions = computed(() =>
  [
    'open',
    'pending',
    'snoozed',
    'resolved',
    ...(isCloudFeatureEnabled('email_spam_filtering') ? ['junk'] : []),
    'all',
  ].map(value => ({
    label: t(`CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.${value}.TEXT`),
    value,
  }))
);

const activeLabel = computed(
  () =>
    statusOptions.value.find(option => option.value === currentStatus.value)
      ?.label || ''
);

// A tinted pill makes a non-default view obvious at a glance
const pillColor = computed(() =>
  currentStatus.value === wootConstants.STATUS_TYPE.OPEN ? 'slate' : 'amber'
);

const handleStatusChange = value => {
  emit('changeFilter', value, 'status');
  store.dispatch('setChatStatusFilter', value);
  updateUISettings({
    conversations_filter_by: {
      status: value,
      order_by:
        chatSortFilter.value ||
        wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC,
    },
  });
};
</script>

<template>
  <SelectMenu
    v-tooltip.bottom="$t('CHAT_LIST.STATUS_FILTER_TOOLTIP')"
    :model-value="currentStatus"
    :options="statusOptions"
    :label="activeLabel"
    :color="pillColor"
    sub-menu-position="bottom"
    class="mx-1 shrink-0"
    @update:model-value="handleStatusChange"
  />
</template>
